import { Link } from 'react-router-dom';
import styles from './Company.module.scss';
import NaverMap from '../../components/common/NaverMap';
import companyBg from '../images/company_bg.jpg';
import { useInView } from '../../hooks/useInView';
import { COMPANY_MENU, CompanySection } from './companyMenu';
import CompanyVision from './CompanyVision';
import { CompanyCertification, CompanyHistory, CompanyPartners } from './CompanyMore';

const ADDRESS = '경상북도 구미시 선산읍 선주로 129-60';
const ADDRESS_COORD = { lat: 36.2436, lng: 128.2942 };
const NAVER_MAP_CLIENT_ID = '87oy2nj0fd';

function Company({ section }: { section: CompanySection }) {
  const visual = useInView<HTMLElement>();
  const greeting = useInView<HTMLElement>();
  const loca = useInView<HTMLElement>();
  const cta = useInView<HTMLElement>();
  const current = COMPANY_MENU.find((m) => m.section === section) ?? COMPANY_MENU[0];

  return (
    <div className={styles.page}>
      {/* PAGE HEAD */}
      <div className={styles.pageHeadWrap}>
        <section className={styles.pageHead}>
          <div className={styles.crumb}>
            <Link to="/">HOME</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <Link to={COMPANY_MENU[0].to}>회사소개</Link>
            <svg viewBox="0 0 24 24">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className={styles.crumbHere}>{current.label}</span>
          </div>
          <h1 className={styles.pageTitle}>{current.label}</h1>
          <p className={styles.pageDesc}>{current.desc}</p>
        </section>
      </div>

      {section === 'greeting' && (
        <>
          {/* COMPANY VISUAL */}
          <section
            ref={visual.ref}
            className={`${styles.companyBg} ${styles.revealLeft} ${visual.inView ? styles.in : ''}`}
            aria-label="G-WORKS 회사소개"
          >
            <img src={companyBg} alt="G-WORKS 컴퍼니 이미지" />
          </section>
    
          {/* GREETING */}
          <section
            ref={greeting.ref}
            className={`${styles.greeting} ${styles.revealRight} ${greeting.inView ? styles.in : ''}`}
          >
            <div className={styles.greetingInner}>
              <div className={styles.greetingHead}>
                <h2 className={styles.greetingTitle}>
                  가치 있는 아이디어를, <br />
                  완성도 높은 결과물로
                </h2>
                <p className={styles.greetingSign}>
                  <span>G-WORKS 대표이사</span>
                  <em className={styles.signName}>변 수 영</em>
                </p>
              </div>
              <div className={styles.greetingBody}>
                <p>안녕하세요. G-WORKS입니다.</p>
                <p>
                  G-WORKS는 기업과 브랜드의 가치를 효과적으로 전달할 수 있도록 기획부터 디자인,
                  <br /> 인쇄 제작까지 체계적인 서비스를 제공하고 있습니다.
                </p>
                <p>
                  단순히 인쇄물을 제작하는 것을 넘어,
                  <br />
                  고객이 전달하고자 하는 메시지와 브랜드의 방향을 이해하고
                  <br />그 가치를 가장 효과적인 형태로 구현하는 것을 중요하게 생각합니다.
                </p>
                <p>
                  패키지, 리플렛, 브로슈어, 포스터 등 다양한 인쇄물을 통해
                  <br />
                  브랜드의 첫인상부터 고객과의 소통까지 세심하게 설계하며,
                  <br />
                  작은 디테일 하나까지 완성도 높은 결과물을 만들어가겠습니다.
                </p>
                <p>
                  빠르게 변화하는 디자인과 인쇄 환경 속에서도
                  <br />
                  끊임없는 고민과 기술력, 그리고 신뢰를 바탕으로
                  <br />
                  고객의 아이디어가 더 큰 가치로 이어질 수 있도록 함께하겠습니다.
                </p>
                <p>
                  G-WORKS는 좋은 아이디어가 좋은 결과로 완성될 때까지
                  <br />
                  고객의 가장 가까운 파트너가 되겠습니다.
                </p>
                <p>감사합니다.</p>
              </div>
            </div>
          </section>
        </>
      )}

      {section === 'vision' && (
        <CompanyVision />
      )}

      {section === 'history' && <CompanyHistory />}

      {section === 'certification' && <CompanyCertification />}

      {section === 'partners' && <CompanyPartners />}

      {section === 'location' && (
        <section ref={loca.ref} className={`${styles.loca} ${styles.revealUp} ${loca.inView ? styles.in : ''}`}>
          <div className={styles.locaTit}>
            <h4>DESIGN &amp; COMPANY</h4>
            <h3>
              <span>G-WORKS</span> 오시는 길
            </h3>
          </div>
          <div className={styles.locaWrap}>
            <div className={styles.locaInfo}>
              <dl>
                <dt>
                  <div>대표번호</div>
                </dt>
                <dd>
                  <span className={styles.locaNum}>053-216-4126</span>
                </dd>
              </dl>
              <dl>
                <dt>
                  <div>영업시간</div>
                </dt>
                <dd className={styles.locaTime}>
                  평일 08:30 – 17:30
                  <small>(점심 12:00–13:00)</small>
                </dd>
              </dl>
              <dl>
                <dt>
                  <div>휴일</div>
                </dt>
                <dd>일요일·공휴일</dd>
              </dl>
              <dl>
                <dt>
                  <div>주소</div>
                </dt>
                <dd>{ADDRESS}</dd>
              </dl>
              <p className={styles.hline} />
              <div className={styles.locaLinks}>
                <a className={styles.naverLink} href="https://naver.me/FTdwseFy" target="_blank" rel="noopener noreferrer">
                  <span className={styles.mapBadge}>N</span>네이버 지도
                </a>
                <a className={styles.kakaoLink} href="https://kko.to/RTvDNqhHzu" target="_blank" rel="noopener noreferrer">
                  <span className={styles.mapBadge}>K</span>카카오 맵
                </a>
              </div>
            </div>
            <div className={styles.locaMap}>
              <NaverMap clientId={NAVER_MAP_CLIENT_ID} title="G-WORKS 오시는 길" address={ADDRESS} fallback={ADDRESS_COORD} />
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section ref={cta.ref} className={`${styles.cta} ${styles.revealUp} ${cta.inView ? styles.in : ''}`}>
        <span className={styles.ctaEyebrow}>LET&apos;S START</span>
        <h2 className={styles.ctaTitle}>
          G-WORKS와 함께
          <br />
          다음 패키지를 만들어보세요
        </h2>
        <p className={styles.ctaDesc}>
          1:1 맞춤 상담을 통해 브랜드에 가장 어울리는 패키지 솔루션을 제안해드립니다.
        </p>
        <div className={styles.ctaActions}>
          <Link className={styles.btnLight} to="/quote">
            견적 문의
          </Link>
          <Link className={styles.btnSolid} to="/works">
            포트폴리오 보기 →
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Company;
